import { StudentSkillStates } from '../../models/StudentSkillState';
import { FoundationSkills } from '../../components/Dashboard/DashboardContainerBody';
import { mapSkillStatus } from '../mapSkillStatus';
import { SkillStatesLabel } from '../../models/SkillState';
import type { SkillGraphNode } from '../getSkillsLevelByUnit';

// A skill within a target's path breakdown: the graph metadata (`SkillGraphNode`) consolidated with
// its full skill info. Matches the shape produced by getSkillsInfoByLevel for the skillInfo built in
// DashboardContainerBody.
export type PathSkillNode = {
  id: number;
  difficulty: number | undefined;
  name: string;
  code: string;
  unitImpact: number | undefined;
  readiness: number | undefined;
} & SkillGraphNode;

// The most cards allowed in a single row. A level with more skills is split across several rows so a
// row never mixes skills from different levels.
const MaxCardsPerRow = 2;

// The dashboard's completion rule, stated over a mapSkillStatus status: "Passed" or "Placed Out".
// Both are needed because mapSkillStatus splits the inferred substates across the two — inferred via
// assessment/exit slip resolve to "Passed" while inferred via diagnostic resolves to "Placed Out" —
// so checking only "Passed" silently treats placed-out skills as unstarted. Shared by connector
// styling, segment counts, and DashboardCard's revisit modal so they can never disagree.
export const isCompletedStatus = (status: string | undefined): boolean =>
  status === SkillStatesLabel.PASSED || status === SkillStatesLabel.PLACED_OUT;

// A skill counts as "passed" for connector styling when its mapSkillStatus status is completed.
export const isPassed = (skill: StudentSkillStates): boolean =>
  isCompletedStatus(mapSkillStatus(skill.skillSubstate?.id, skill.skillAssessment?.score).status);

// A skill "has a score" when it carries an assessment score (0 counts). A scored skill always has
// something to show, so it must never render as blocked/locked.
export const hasScore = (skill: StudentSkillStates): boolean =>
  skill.skillAssessment?.score !== null && skill.skillAssessment?.score !== undefined;

/**
 * Reorders the target → level → skill breakdown before rendering:
 *  1. reverses the outer array (order of the paths/targets),
 *  2. reverses each path's levels (foundations end up on top, the target at the bottom),
 *  3. sorts each level's skills by readiness descending (undefined readiness sinks to the end), breaking
 *     ties by difficulty ascending (undefined difficulty sinks to the end).
 * Uses non-mutating copies throughout.
 */
export const orderPaths = (paths: PathSkillNode[][][]): PathSkillNode[][][] =>
  [...paths]
    .reverse()
    .map(path =>
      [...path]
        .reverse()
        .map(level =>
          [...level].sort(
            (a, b) =>
              (b.readiness ?? -Infinity) - (a.readiness ?? -Infinity) ||
              (a.difficulty ?? Number.POSITIVE_INFINITY) - (b.difficulty ?? Number.POSITIVE_INFINITY),
          ),
        ),
    );

/** Splits a single level into rows of at most MaxCardsPerRow cards. */
export const chunkIntoRows = <T>(level: T[]): T[][] => {
  const rows: T[][] = [];
  for (let index = 0; index < level.length; index += MaxCardsPerRow) {
    rows.push(level.slice(index, index + MaxCardsPerRow));
  }
  return rows;
};

/**
 * Flattens a path's levels into a single top-to-bottom list of rows. Chunking happens per level, so
 * no row ever spans two levels. Empty rows are dropped.
 */
export const buildRows = (path: PathSkillNode[][]): PathSkillNode[][] =>
  path.flatMap(level => chunkIntoRows(level)).filter(row => row.length > 0);

// Consolidates every target into its levels: reorders the input, drops skills already shown in the
// foundations panel, resolves each remaining node to its StudentSkillStates by id (no Redux), and
// drops empty levels. The result is target → level → skill; row chunking happens later, at render.
export const consolidatePaths = (
  paths: PathSkillNode[][][],
  studentSkills: StudentSkillStates[],
  foundationSkills: FoundationSkills[],
): StudentSkillStates[][][] => {
  const skillStateById = new Map(studentSkills.map(skill => [skill.skill.id, skill]));
  const foundationSkillIds = new Set(foundationSkills.map(skill => skill.id));

  return orderPaths(paths)
    .map(path =>
      path
        .map(level =>
          level
            .filter(node => !foundationSkillIds.has(node.id))
            .map(node => skillStateById.get(`SkillNode:${node.id}`))
            .filter((skill): skill is StudentSkillStates => skill !== undefined),
        )
        .filter(level => level.length > 0),
    )
    .filter(levels => levels.length > 0);
};

// Cards in the first / last rendered row of a path's levels (rows hold at most MaxCardsPerRow cards).
export const firstRowSize = (levels: StudentSkillStates[][]): number => Math.min(levels[0].length, MaxCardsPerRow);
export const lastRowSize = (levels: StudentSkillStates[][]): number => {
  const lastLevel = levels[levels.length - 1];
  return ((lastLevel.length - 1) % MaxCardsPerRow) + 1;
};

// Whether every foundation skill shown in the panel is passed. Unmatched foundations count as not
// passed, so the paths stay locked until the whole foundations set is complete.
export const areFoundationsAllPassed = (
  foundationSkills: FoundationSkills[],
  studentSkills: StudentSkillStates[],
): boolean => {
  const skillStateById = new Map(studentSkills.map(skill => [skill.skill.id, skill]));
  return foundationSkills.every(foundation => {
    const state = skillStateById.get(`SkillNode:${foundation.id}`);
    return state !== undefined && isPassed(state);
  });
};

// Cards in the last row that the foundations panel renders for `foundationCount` skills. The panel
// lays a single card first and then pairs the rest, so the last row holds a single card for an even
// count (and for a lone foundation); an odd count of three or more ends on a full pair of two.
export const foundationsLastRowSize = (foundationCount: number): number => {
  if (foundationCount <= 0) {
    return 0;
  }
  return foundationCount >= 3 && foundationCount % 2 === 1 ? 2 : 1;
};
